// Runtime config accessor. The values (Cognito user pool id, SPA client id,
// region, API base URL) are NON-SECRET public identifiers, but they still must
// not be hardcoded in source — they differ per stage and per deploy. They are
// injected at runtime by `ui/public/config.js` (generated from CloudFormation
// stack outputs by `npm run get-config`), which sets `window.SPARKY_CONFIG`.
//
// See ui/public/config.example.js for the shape and ui/README.md for how it is
// populated. No API keys, tokens, or secrets ever live in config — auth tokens
// come from Cognito at sign-in and are held by the Cognito SDK.

const REQUIRED_KEYS = ['region', 'userPoolId', 'userPoolClientId', 'apiBaseUrl'];
// The voice config keys (`identityPoolId`, `voiceRuntimeArn`) are OPTIONAL: they
// aren't required for the app to boot, so voice mode degrades to typed input when
// either is absent (Req 11.6) rather than blocking sign-in. See getVoiceConfig()
// below. (The old `wssUrl` key was retired — voice now presigns the runtime socket
// directly; see the sparky-voice spec, task 9.3.)

export class ConfigError extends Error {
  constructor(missingKeys) {
    super(
      `Sparky is not configured. Missing/placeholder config: ${missingKeys.join(', ')}. ` +
        'Generate ui/public/config.js with `npm run get-config` (or copy config.example.js).',
    );
    this.name = 'ConfigError';
    this.missingKeys = missingKeys;
  }
}

function rawConfig() {
  return (typeof window !== 'undefined' && window.SPARKY_CONFIG) || {};
}

// A key is "unset" if it's empty or still holds a REPLACE_* placeholder from the
// example file.
function isUnset(value) {
  return !value || typeof value !== 'string' || value.startsWith('REPLACE_');
}

// Returns the validated config, or throws ConfigError listing what's missing.
export function getConfig() {
  const cfg = rawConfig();
  const missing = REQUIRED_KEYS.filter((k) => isUnset(cfg[k]));
  if (missing.length > 0) throw new ConfigError(missing);
  return {
    region: cfg.region,
    userPoolId: cfg.userPoolId,
    userPoolClientId: cfg.userPoolClientId,
    apiBaseUrl: cfg.apiBaseUrl.replace(/\/+$/, ''), // normalise trailing slash
    // Voice (sparky-voice spec) — OPTIONAL, populated by task 8 (get-config.sh).
    // Read defensively here so 2.3 can consume them before 8 lands: unset ⇒ voice
    // disabled ⇒ typed fallback (Req 11.6, 12.x). Never hardcoded.
    identityPoolId: isUnset(cfg.identityPoolId) ? null : cfg.identityPoolId,
    voiceRuntimeArn: isUnset(cfg.voiceRuntimeArn) ? null : cfg.voiceRuntimeArn,
  };
}

// The Cognito Identity Pool id + voice runtime ARN needed to presign the voice
// WebSocket, or null when either is absent. Voice mode calls this and falls back to
// typed input when it returns null (Req 11.6, 12.x). Never throws.
export function getVoiceConfig() {
  let cfg;
  try {
    cfg = getConfig();
  } catch {
    return null;
  }
  if (!cfg.identityPoolId || !cfg.voiceRuntimeArn) return null;
  return {
    region: cfg.region,
    userPoolId: cfg.userPoolId,
    identityPoolId: cfg.identityPoolId,
    voiceRuntimeArn: cfg.voiceRuntimeArn,
  };
}

export function isConfigured() {
  try {
    getConfig();
    return true;
  } catch {
    return false;
  }
}

// Human-readable reason config is unusable (for the "not configured" screen).
export function getConfigError() {
  try {
    getConfig();
    return null;
  } catch (err) {
    return err.message;
  }
}
